import SearchIcon from '@mui/icons-material/Search'
import { Box, InputAdornment, TextField, Typography, useTheme } from '@mui/material'
import { useProducts } from 'api/aws/useProducts'
import React, { useMemo, useState } from 'react'

const fmt = (v: number | string | undefined): string => {
  const n = typeof v === 'string' ? parseFloat(v) : v
  if (n == null || isNaN(n)) return '--'
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
}

const ProductReferenceContent: React.FC = () => {
  const theme = useTheme()
  const { data: products = [] } = useProducts()
  const [search, setSearch] = useState('')

  const filtered = useMemo(() => {
    const term = search.toLowerCase().trim()
    return products
      .filter(p => !p.isArchived)
      .filter(p => {
        if (!term) return true
        return (
          p.title?.toLowerCase().includes(term) ||
          p.productName?.toLowerCase().includes(term) ||
          p.category_name?.toLowerCase().includes(term) ||
          p.description?.toLowerCase().includes(term)
        )
      })
  }, [products, search])

  const grouped = useMemo(() => {
    const groups: Record<string, typeof filtered> = {}
    for (const p of filtered) {
      const cat = p.category_name || p.category || 'Uncategorized'
      if (!groups[cat]) groups[cat] = []
      groups[cat].push(p)
    }
    return Object.entries(groups)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(
        ([cat, items]) =>
          [
            cat,
            items.sort((a, b) => (a.title || a.productName || '').localeCompare(b.title || b.productName || '')),
          ] as [string, typeof filtered]
      )
  }, [filtered])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ px: 1.5, py: 1, flexShrink: 0 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search products..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 1.5,
              fontSize: '0.8rem',
              bgcolor: 'background.default',
            },
          }}
        />
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 1,
          pb: 1,
          '&::-webkit-scrollbar': { width: 5 },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: theme.palette.background.scrollbar_thumb,
            borderRadius: 3,
          },
        }}
      >
        {grouped.length === 0 && (
          <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', textAlign: 'center', mt: 4 }}>
            No products found
          </Typography>
        )}
        {grouped.map(([category, items]) => (
          <Box key={category} sx={{ mb: 1.5 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                fontSize: '0.65rem',
                px: 0.5,
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              {category}
              <Box component="span" sx={{ color: 'text.disabled' }}>
                ({items.length})
              </Box>
            </Typography>

            {items.map((product, i) => (
              <Box
                key={product.SK || i}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto auto',
                  gap: 1,
                  px: 0.5,
                  py: 0.4,
                  borderRadius: 1,
                  '&:hover': {
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                  },
                }}
              >
                <Typography noWrap variant="body2" sx={{ fontSize: '0.78rem', fontWeight: 500, textAlign: 'left' }}>
                  {product.title || product.productName || '--'}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontSize: '0.75rem', color: 'text.success', fontWeight: 500, textAlign: 'right' }}
                >
                  {fmt(product.price)}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontSize: '0.75rem', color: 'text.secondary', textAlign: 'right', minWidth: 36 }}
                >
                  {typeof product.margin === 'number' || typeof product.margin === 'string'
                    ? `${product.margin}%`
                    : '--'}
                </Typography>
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  )
}

export default ProductReferenceContent
